import React, { useState, useRef, useMemo } from 'react';
import { Input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import { PiCalendar } from 'react-icons/pi';
import { Calendar } from './Calendar';

// Configuration for different date formats
interface DateFormatConfig {
  pattern: string;
  separator: string;
  segments: Array<{
    type: 'year' | 'month' | 'day';
    startPos: number;
    endPos: number;
    maxValue?: number;
    minValue?: number;
  }>;
  separatorPositions: number[];
}

// Predefined format configurations
const DATE_FORMATS: Record<string, DateFormatConfig> = {
  'yyyy-MM-dd': {
    pattern: 'yyyy-MM-dd',
    separator: '-',
    segments: [
      { type: 'year', startPos: 0, endPos: 3 },
      { type: 'month', startPos: 5, endPos: 6, minValue: 1, maxValue: 12 },
      { type: 'day', startPos: 8, endPos: 9, minValue: 1, maxValue: 31 },
    ],
    separatorPositions: [4, 7],
  },
  'MM-dd-yyyy': {
    pattern: 'MM-dd-yyyy',
    separator: '-',
    segments: [
      { type: 'month', startPos: 0, endPos: 1, minValue: 1, maxValue: 12 },
      { type: 'day', startPos: 3, endPos: 4, minValue: 1, maxValue: 31 },
      { type: 'year', startPos: 6, endPos: 9 },
    ],
    separatorPositions: [2, 5],
  },
  'dd-MM-yyyy': {
    pattern: 'dd-MM-yyyy',
    separator: '-',
    segments: [
      { type: 'day', startPos: 0, endPos: 1, minValue: 1, maxValue: 31 },
      { type: 'month', startPos: 3, endPos: 4, minValue: 1, maxValue: 12 },
      { type: 'year', startPos: 6, endPos: 9 },
    ],
    separatorPositions: [2, 5],
  },
  'MM/dd/yyyy': {
    pattern: 'MM/dd/yyyy',
    separator: '/',
    segments: [
      { type: 'month', startPos: 0, endPos: 1, minValue: 1, maxValue: 12 },
      { type: 'day', startPos: 3, endPos: 4, minValue: 1, maxValue: 31 },
      { type: 'year', startPos: 6, endPos: 9 },
    ],
    separatorPositions: [2, 5],
  },
  'dd/MM/yyyy': {
    pattern: 'dd/MM/yyyy',
    separator: '/',
    segments: [
      { type: 'day', startPos: 0, endPos: 1, minValue: 1, maxValue: 31 },
      { type: 'month', startPos: 3, endPos: 4, minValue: 1, maxValue: 12 },
      { type: 'year', startPos: 6, endPos: 9 },
    ],
    separatorPositions: [2, 5],
  },
};

// Helper class to manage date formatting and parsing
class DateFormatManager {
  private config: DateFormatConfig;

  constructor(format: string) {
    this.config = DATE_FORMATS[format];
    if (!this.config) {
      throw new Error(`Unsupported date format: ${format}`);
    }
  }

  format(date: Date | undefined): string {
    if (!date) return '';

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return this.config.pattern
      .replace(/yyyy/g, year.toString())
      .replace(/MM/g, month.toString().padStart(2, '0'))
      .replace(/dd/g, day.toString().padStart(2, '0'));
  }

  parse(value: string): Date | undefined {
    if (!value) return undefined;

    // Replace underscores with zeros for parsing
    const cleanValue = value.replace(/_/g, '0');

    // Extract segments based on configuration
    const segments: Record<string, number> = {};

    for (const segment of this.config.segments) {
      const segmentValue = cleanValue.substring(
        segment.startPos,
        segment.endPos + 1,
      );
      segments[segment.type] =
        parseInt(segmentValue) || (segment.type === 'year' ? 2024 : 1);
    }

    try {
      const date = new Date(segments.year, segments.month - 1, segments.day);
      return this.isValidDate(date) ? date : undefined;
    } catch {
      return undefined;
    }
  }

  getSegmentAtPosition(
    position: number,
  ): { type: 'year' | 'month' | 'day'; segment: any } | null {
    for (const segment of this.config.segments) {
      if (position >= segment.startPos && position <= segment.endPos) {
        return { type: segment.type, segment };
      }
    }
    return null;
  }

  validateDigitInput(
    position: number,
    digit: number,
    currentValue: string,
  ): boolean {
    const segmentInfo = this.getSegmentAtPosition(position);
    if (!segmentInfo) return false;

    const { segment } = segmentInfo;
    const dateArray = currentValue.split('');
    dateArray[position] = digit.toString();

    // Get the current segment value after the change
    const segmentValue = dateArray
      .slice(segment.startPos, segment.endPos + 1)
      .join('');
    const numericValue = parseInt(segmentValue);

    // Basic range validation
    if (
      segment.minValue &&
      numericValue < segment.minValue &&
      !segmentValue.includes('_')
    ) {
      return false;
    }
    if (segment.maxValue && numericValue > segment.maxValue) {
      return false;
    }

    // Special validation for months and days
    if (segmentInfo.type === 'month') {
      const firstDigit = parseInt(dateArray[segment.startPos]);
      const secondDigit = digit;

      if (position === segment.startPos && digit > 1) return false;
      if (position === segment.endPos && firstDigit === 1 && secondDigit > 2)
        return false;
      if (position === segment.endPos && firstDigit === 0 && secondDigit === 0)
        return false;
    }

    if (segmentInfo.type === 'day') {
      const firstDigit = parseInt(dateArray[segment.startPos]);
      const secondDigit = digit;

      if (position === segment.startPos && digit > 3) return false;
      if (position === segment.endPos && firstDigit === 3 && secondDigit > 1)
        return false;
      if (position === segment.endPos && firstDigit === 0 && secondDigit === 0)
        return false;
    }

    return true;
  }

  convertUnderscoresToZeros(dateStr: string): string {
    let result = dateStr.replace(/_/g, '0');

    // Extract and validate each segment
    const segments: Record<string, number> = {};

    for (const segment of this.config.segments) {
      let value =
        parseInt(result.substring(segment.startPos, segment.endPos + 1)) || 1;

      if (segment.minValue) value = Math.max(segment.minValue, value);
      if (segment.maxValue) value = Math.min(segment.maxValue, value);

      segments[segment.type] = value;
    }

    // Special handling for day validation based on month
    if (segments.month && segments.day && segments.year) {
      const lastDayOfMonth = new Date(
        segments.year,
        segments.month,
        0,
      ).getDate();
      segments.day = Math.min(segments.day, lastDayOfMonth);
    }

    // Reconstruct the date string
    return this.config.pattern
      .replace(/yyyy/g, segments.year.toString().padStart(4, '0'))
      .replace(/MM/g, segments.month.toString().padStart(2, '0'))
      .replace(/dd/g, segments.day.toString().padStart(2, '0'));
  }

  getDefaultFocusPosition(): number {
    // Find the day segment for default focus
    const daySegment = this.config.segments.find((s) => s.type === 'day');
    return daySegment ? daySegment.startPos : 0;
  }

  getSeparatorPositions(): number[] {
    return this.config.separatorPositions;
  }

  private isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }
}

// Format function that handles any date format string
const formatDateWithPattern = (
  date: Date | undefined,
  pattern: string,
): string => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return pattern
    .replace(/yyyy/g, year.toString())
    .replace(/MM/g, month.toString().padStart(2, '0'))
    .replace(/dd/g, day.toString().padStart(2, '0'));
};

interface DatePickerWithInputProps {
  id?: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  dateFormat?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  disableDate?: (date: Date) => boolean;
}

function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

export function DatePickerWithInput({
  id,
  value,
  onChange,
  dateFormat = 'yyyy-MM-dd',
  placeholder,
  className = '',
  disabled = false,
  disableDate,
}: DatePickerWithInputProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [month, setMonth] = useState<Date>(value || new Date());
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Initialize format manager
  const formatManager = new DateFormatManager(dateFormat);

  // Calculate dynamic year range - current year and 25 years back
  const { fromYear, toYear } = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return {
      fromYear: currentYear - 25,
      toYear: currentYear,
    };
  }, []);

  const [dateValue, setDateValue] = useState<string>(() => {
    return formatManager.format(value);
  });

  const getPlaceholder = (): string => {
    if (placeholder) return placeholder;
    return dateFormat || 'Select date';
  };

  const triggerDateChange = (newDate: string, forceUpdate = false) => {
    setDateValue(newDate);

    if ((onChange && !newDate.includes('_')) || forceUpdate) {
      const parsedDate = formatManager.parse(newDate);
      if (parsedDate && isValidDate(parsedDate)) {
        setMonth(parsedDate);
        onChange(parsedDate);
      }
    }
  };

  const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDateValue(inputValue);

    if (onChange && !inputValue.includes('_')) {
      const parsedDate = formatManager.parse(inputValue);
      if (parsedDate && isValidDate(parsedDate)) {
        setMonth(parsedDate);
        onChange(parsedDate);
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const cleanDate = formatManager.convertUnderscoresToZeros(dateValue);
    if (cleanDate !== dateValue) {
      triggerDateChange(cleanDate, true);
    }
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const formatted = formatManager.format(selectedDate);
      setDateValue(formatted);
      setMonth(selectedDate);
      onChange?.(selectedDate);
      setOpen(false);
    }
  };

  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const segmentInfo = formatManager.getSegmentAtPosition(cursorPos);

    if (['Tab', 'Enter', 'Escape'].includes(e.key)) {
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      setTimeout(() => {
        const pos = input.selectionStart || 0;
        const separatorPositions = formatManager.getSeparatorPositions();

        separatorPositions.forEach((sepPos) => {
          if (pos === sepPos && e.key === 'ArrowRight') {
            input.setSelectionRange(sepPos + 1, sepPos + 1);
          } else if (pos === sepPos + 1 && e.key === 'ArrowLeft') {
            input.setSelectionRange(sepPos, sepPos);
          }
        });
      }, 0);
      return;
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const increment = e.key === 'ArrowUp' ? 1 : -1;

      const parsedDate = formatManager.parse(dateValue);
      if (!parsedDate || !isValidDate(parsedDate) || !segmentInfo) {
        return;
      }

      let newDate = new Date(parsedDate);

      if (segmentInfo.type === 'year') {
        newDate.setFullYear(newDate.getFullYear() + increment);
      } else if (segmentInfo.type === 'month') {
        const currentMonth = newDate.getMonth();
        const currentDay = newDate.getDate();
        const currentYear = newDate.getFullYear();
        let targetMonth = currentMonth + increment;
        let targetYear = currentYear;

        if (targetMonth < 0) {
          targetMonth = 11;
          targetYear = currentYear - 1;
        } else if (targetMonth > 11) {
          targetMonth = 0;
          targetYear = currentYear + 1;
        }

        const lastDayOfTargetMonth = new Date(
          targetYear,
          targetMonth + 1,
          0,
        ).getDate();
        const targetDay = Math.min(currentDay, lastDayOfTargetMonth);
        newDate = new Date(targetYear, targetMonth, targetDay);
      } else if (segmentInfo.type === 'day') {
        const lastDayOfMonth = new Date(
          newDate.getFullYear(),
          newDate.getMonth() + 1,
          0,
        ).getDate();
        const currentDay = newDate.getDate();
        const newDay = currentDay + increment;

        if (newDay < 1) {
          newDate.setMonth(newDate.getMonth() - 1);
          const prevMonthLastDay = new Date(
            newDate.getFullYear(),
            newDate.getMonth() + 1,
            0,
          ).getDate();
          newDate.setDate(prevMonthLastDay);
        } else if (newDay > lastDayOfMonth) {
          newDate.setMonth(newDate.getMonth() + 1);
          newDate.setDate(1);
        } else {
          newDate.setDate(newDay);
        }
      }

      const formattedDate = formatManager.format(newDate);
      triggerDateChange(formattedDate);
      setTimeout(() => input.setSelectionRange(cursorPos, cursorPos), 0);
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      if (cursorPos === 0) return;

      const newCursorPos = cursorPos - 1;
      const separatorPositions = formatManager.getSeparatorPositions();

      if (!separatorPositions.includes(cursorPos)) {
        const dateArray = dateValue.split('');
        dateArray[cursorPos - 1] = '_';
        const newDate = dateArray.join('');
        triggerDateChange(newDate);
      }

      setTimeout(() => input.setSelectionRange(newCursorPos, newCursorPos), 0);
      return;
    }

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const digit = parseInt(e.key);
      const separatorPositions = formatManager.getSeparatorPositions();

      if (separatorPositions.includes(cursorPos)) {
        return;
      }

      if (cursorPos < dateValue.length) {
        const isValid = formatManager.validateDigitInput(
          cursorPos,
          digit,
          dateValue,
        );

        if (isValid) {
          const dateArray = dateValue.split('');
          dateArray[cursorPos] = digit.toString();
          const newDateStr = dateArray.join('');
          triggerDateChange(newDateStr);

          let newCursorPos = cursorPos + 1;
          if (separatorPositions.includes(cursorPos + 1)) {
            newCursorPos = cursorPos + 2;
          }

          setTimeout(
            () => input.setSelectionRange(newCursorPos, newCursorPos),
            0,
          );
        } else {
          setTimeout(() => input.setSelectionRange(cursorPos, cursorPos), 0);
        }
      }
      return;
    }

    e.preventDefault();
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    setTimeout(() => {
      const focusPosition = formatManager.getDefaultFocusPosition();
      input.setSelectionRange(focusPosition, focusPosition);
    }, 0);
  };

  const handleDateClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    setTimeout(() => {
      const cursorPos = input.selectionStart || 0;
      const separatorPositions = formatManager.getSeparatorPositions();

      separatorPositions.forEach((sepPos) => {
        if (cursorPos === sepPos) {
          const rect = input.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const charWidth = rect.width / dateValue.length;
          const sepCenter = charWidth * (sepPos + 0.5);

          input.setSelectionRange(
            x < sepCenter ? sepPos : sepPos + 1,
            x < sepCenter ? sepPos : sepPos + 1,
          );
        }
      });
    }, 0);
  };

  return (
    <div className={`relative flex gap-2 w-36 ${className}`}>
      <Input
        ref={dateInputRef}
        id="date"
        value={dateValue}
        placeholder={getPlaceholder()}
        className="bg-background pr-10 font-mono"
        onChange={handleDateInputChange}
        onKeyDown={handleDateKeyDown}
        onClick={handleDateClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        maxLength={10}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id ? `${id}-calendar-button` : undefined}
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
          >
            <PiCalendar className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto overflow-hidden p-0"
          align="end"
          alignOffset={-8}
          sideOffset={10}
        >
          <Calendar
            mode="single"
            selected={value}
            captionLayout="dropdown"
            fromYear={fromYear}
            toYear={toYear}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleDateSelect}
            disabled={disableDate}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
